import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, AddIcon, MinusIcon } from "@chakra-ui/icons";
import { FaUser, FaTrain } from "react-icons/fa";
import { FiArrowUpRight, FiArrowDownLeft, FiRefreshCw } from "react-icons/fi";
import Translate from "../../LanguageTranslation/Translate";
import axiosInstance from "../../../helpers/axiosInstance";

/* ---------- shared helpers ---------- */
const BRAND = "#286328";
const BRAND_FILL = "rgba(40,99,40,0.10)";
const LABEL_W = "220px";
const FIELD_W = "240px";

const DD_PROPS = {
  variant: "ghost",
  fontWeight: "bold",
  px: 4,
  py: 2,
  border: "1px solid #d8d8d8",
  borderRadius: "md",
  bg: "#FFFBEF",
  _hover: { bg: "#F5F5F5" },
};

const tripTypeLabel = {
  round: "Round Trip",
  one: "One Way",
  multi: "Multi-city",
};
const PAX_VALUES = Array.from({ length: 9 }, (_, i) => i + 1);
const CLASS_OPTS = [
  { key: "economy", label: "Economy" },
  { key: "1st class", label: "1st class" },
  { key: "business", label: "Business" },
  { key: "sleeper", label: "Sleeper" },
];

/* duration buckets for multi-trip */
const TRAIN_ROWS = [
  { key: "short", label: "Short Trips", sub: "< 2 hours" },
  { key: "medium", label: "Medium Trips", sub: "2-4 hours" },
  { key: "long", label: "Long Trips", sub: "4+ hours" },
];

/* -------------------------------------------------------------------------- */
const TrainTripDetails = ({
  tripMode,
  setTripMode,
  tripType,
  setTripType,
  passengers,
  setPassengers,
  trainClass,
  setTrainClass,
  from,
  setFrom,
  to,
  setTo,
  counts,
  setCounts,
  swap,
  trainResult,
  setTrainResult,
  selectedTrip,
  hasTrips,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inc = (k) => setCounts((p) => ({ ...p, [k]: p[k] + 1 }));
  const dec = (k) => setCounts((p) => ({ ...p, [k]: Math.max(0, p[k] - 1) }));

  const handleCalculate = async () => {
    // Check if trip is selected / any trips exist
    if (!selectedTrip || selectedTrip.trim() === '') {
      alert(hasTrips
        ? 'Please select a trip before calculating emissions.'
        : 'No trips found. Please create a trip plan first, then select it before calculating emissions.'
      );
      return;
    }

    if (tripMode === "one" && (!from || !to)) {
      setError("Please enter departure and destination locations");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (tripMode === "multi") {
        const payload = {
          short_trips: counts.short,
          medium_trips: counts.medium,
          long_trips: counts.long,
          train_class: trainClass,
        };
        const { data } = await axiosInstance.post(
          "/co2-emissions/calculate/multiple-trains",
          payload
        );
        // Normalize for consumers expecting emissions_kg_co2e
        setTrainResult({
          ...data,
          emissions_kg_co2e: data.total_emissions_kg_co2e,
        });
      } else {
        const payload = {
          departure_location: from,
          destination_location: to,
          is_return_trip: tripType === "round",
          number_of_passengers: passengers,
          train_class: trainClass,
          environmental_cause: "reforestation", // Default cause
        };
        const response = await axiosInstance.post(
          "/co2-emissions/calculate/train",
          payload
        );
        setTrainResult(response.data);
      }
    } catch (err) {
      console.error("Error calculating train emissions:", err);
      setError(err.response?.data?.error || "Failed to calculate emissions");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  return (
    <>
      {/* One- / Multi-trip toggle */}
      <RadioGroup value={tripMode} onChange={setTripMode} mb={8}>
        <HStack spacing={6}>
          <Radio value="one" colorScheme="green">
            <Translate text="One Trip" />
          </Radio>
          <Radio value="multi" colorScheme="green">
            <Translate text="Multiple Trips" />
          </Radio>
        </HStack>
      </RadioGroup>

      {/* ───────────────── ONE-TRIP FORM ───────────────────── */}
      {tripMode === "one" && (
        <>
          {/* dropdown trio */}
          <HStack spacing={4} mb={8} flexWrap="wrap">
            {/* trip type */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                {...DD_PROPS}
              >
                <Translate text={tripTypeLabel[tripType]} />
              </MenuButton>
              <MenuList>
                {Object.entries(tripTypeLabel).map(([k, v]) => (
                  <MenuItem key={k} onClick={() => setTripType(k)}>
                    <Translate text={v} />
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            {/* passengers */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                {...DD_PROPS}
              >
                <HStack>
                  <Icon as={FaUser} boxSize={3} />
                  <Text mb={0}>
                    <Translate text={passengers} />
                  </Text>
                </HStack>
              </MenuButton>
              <MenuList>
                {PAX_VALUES.map((n) => (
                  <MenuItem key={n} onClick={() => setPassengers(n)}>
                    <HStack>
                      <Icon as={FaUser} boxSize={3} />
                      <Text mb={0}>
                        <Translate text={n} />
                      </Text>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            {/* class */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                {...DD_PROPS}
              >
                {CLASS_OPTS.find((c) => c.key === trainClass)?.label}
              </MenuButton>
              <MenuList>
                {CLASS_OPTS.map((c) => (
                  <MenuItem key={c.key} onClick={() => setTrainClass(c.key)}>
                    {c.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </HStack>

          {/* route inputs */}
          <HStack w="full" mb={8} spacing={4}>
            <InputGroup flex="1">
              <InputLeftElement
                pointerEvents="none"
                py={8}
                color="gray.500"
                children={<FiArrowUpRight />}
              />
              <Input
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="Where From?"
                border="1px solid #d8d8d8"
                _focus={{ borderColor: BRAND }}
                borderRadius="md"
                py={8}
              />
            </InputGroup>

            <IconButton
              aria-label="swap"
              icon={<FiRefreshCw />}
              variant="ghost"
              onClick={swap}
              color="gray.600"
              _hover={{ color: BRAND }}
            />

            <InputGroup flex="1">
              <InputLeftElement
                pointerEvents="none"
                py={8}
                color="gray.500"
                children={<FiArrowDownLeft />}
              />
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="Where To?"
                border="1px solid #d8d8d8"
                _focus={{ borderColor: BRAND }}
                borderRadius="md"
                py={8}
              />
            </InputGroup>
          </HStack>
        </>
      )}

      {/* ───────────────── MULTI-TRIP FORM ─────────────────── */}
      {tripMode === "multi" && (
        <>
          <Heading size="sm" mb={6}>
            <Translate text="How many round-trip train journeys would you like to offset?" />
          </Heading>

          {/* Round-trip dropdown placeholder (if you need other legs later) */}
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              {...DD_PROPS}
              mb={6}
            >
              <Translate text={tripTypeLabel[tripType]} />
            </MenuButton>
            <MenuList>
              {Object.entries(tripTypeLabel).map(([k, v]) => (
                <MenuItem key={k} onClick={() => setTripType(k)}>
                  <Translate text={v} />
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          <VStack align="stretch" spacing={6} mb={8}>
            {TRAIN_ROWS.map(({ key, label, sub }) => (
              <Flex key={key} align="center">
                <Icon as={FaTrain} mr={4} color="gray.700" />

                <Box flex="1">
                  <Text fontWeight="bold" mb={0}>
                    <Translate text={label} />
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={0}>
                    <Translate text={sub} />
                  </Text>
                </Box>

                <IconButton
                  aria-label="minus"
                  icon={<MinusIcon />}
                  size="sm"
                  variant="ghost"
                  border="1px solid"
                  borderColor={BRAND}
                  color={BRAND}
                  isDisabled={counts[key] === 0}
                  onClick={() => dec(key)}
                  _hover={{ bg: "transparent" }}
                />

                <Text w="32px" textAlign="center" mb={0}>
                  {counts[key]}
                </Text>

                <IconButton
                  aria-label="plus"
                  icon={<AddIcon />}
                  size="sm"
                  variant="ghost"
                  border="1px solid"
                  borderColor={BRAND}
                  color={BRAND}
                  onClick={() => inc(key)}
                  _hover={{ bg: "transparent" }}
                />
              </Flex>
            ))}
          </VStack>
        </>
      )}

      {/* Error message */}
      {error && (
        <Text color="red.500" mb={4} fontSize="sm">
          {error}
        </Text>
      )}

      {/* CTA */}
      <Divider mb={8} />

      <Button
        w="full"
        bg={BRAND}
        color="white"
        _hover={{ bg: BRAND }}
        borderRadius="lg"
        py={6}
        fontWeight="bold"
        onClick={handleCalculate}
        isLoading={loading}
        // isDisabled={tripMode === "one" && (!from || !to)}
      >
        <Translate text="Calculate" />
      </Button>
    </>
  );
};

export default TrainTripDetails;
